import { VideoJsPlayer } from 'video.js';
import { Icon, Text } from '../../../styles';
import { __t } from '../../../i18n';
import { IconLive, IconSkipNext } from '../../../icons';
import { useEffect, useRef, useState } from 'react';
import theme from '../../../theme';
import { BadgeWrapper } from '../styles';

const LiveBadge = ({ player }: { player: VideoJsPlayer }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isBehind, setIsBehind] = useState(true);

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    handleMobileBreakpoint();
    // Mirror the progress bar: read the player's live-edge state, which already
    // accounts for paused playback, drifting behind the edge, and edge jitter.
    handleLiveState();
    player.on('timeupdate', handleLiveState);
    player.on('pause', handleLiveState);
    player.on('play', handleLiveState);
    player.on('seeked', handleLiveState);
    window.addEventListener('resize', throttledHandle, { passive: true });
    return () => {
      player.off('timeupdate', handleLiveState);
      player.off('pause', handleLiveState);
      player.off('play', handleLiveState);
      player.off('seeked', handleLiveState);
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleMobileBreakpoint();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
  };

  const handleLiveState = () => {
    const liveTrackerBehind = player.liveTracker?.behindLiveEdge();
    // liveTracker is unreliable on native players (iOS, legacy TVs).
    // Fall back to a direct seekable comparison using liveWindow as tolerance.
    // liveWindow() is VideoJS's own live-edge window size; if unavailable, use 10% of the DVR window.
    const seekable = player.seekable();
    let seekableBehind = false;
    if (!liveTrackerBehind && seekable && seekable.length > 0) {
      const liveWindow = (player.liveTracker as any)?.liveWindow?.() ?? 0;
      const seekableWindow = seekable.end(0) - seekable.start(0);
      const tolerance = liveWindow > 0 ? liveWindow : Math.max(seekableWindow * 0.1, 3);
      seekableBehind = player.currentTime() < seekable.end(0) - tolerance;
    }
    setIsBehind(Boolean(liveTrackerBehind || seekableBehind));
  };

  const goToLive = () => {
    if (!isBehind) return;
    if (player.liveTracker) {
      player.liveTracker.seekToLiveEdge();
    } else {
      const seekable = player.seekable();
      if (seekable && seekable.length > 0) {
        player.currentTime(seekable.end(0));
      }
    }
  };

  return <BadgeWrapper
    justify='center'
    align='center'
    mr={isMobile ? '0.75rem' : '1.5rem'}
    isBehind={isBehind}
    onClick={goToLive}
  >
    <Text variant='body2' weight='bold' mr='0.5rem'>
      {__t('player.live')}
    </Text>
    {isBehind ? <Icon size='semiSmall' cursor={isBehind ? 'pointer' : 'inherit'}>
      <IconSkipNext/>
    </Icon> : <Icon size='small' cursor={isBehind ? 'pointer' : 'inherit'}>
      <IconLive/>
    </Icon>}
  </BadgeWrapper>;
};

export default LiveBadge;
